/**
 * The Upload Studio's save queue (Phase 91, 91-09; D-01, D-02, UI-SPEC
 * section 3 Saving / Saved / Put back / Failed / Offline rows).
 *
 * Save and next hands the product's saves here and opens the next product at
 * once; the page never waits on the server. The queue lives on the page,
 * above the canvas screen, so moving between products never drops a save.
 *
 *  - Sent at once, one at a time, in the order saved: no browser-side delay,
 *    no undo window, no cancel. The server stores the position and publishes
 *    straight away (91-04). A save whose file is still uploading waits for its
 *    commit (the sha256), and only it waits: a later product's save may pass
 *    it, never a later save of the same product.
 *  - Put back last position is one ordinary 'place' of the slot's previous
 *    values at its current version, through this same queue (so a shared
 *    position is refused unconfirmed and a stale one by the server's CAS). A
 *    second press while it is still unsent replaces it (`replaceKey`).
 *  - A refusal the reuse dialogs answer (shared change unconfirmed, a
 *    placement conflict, too many linked) stays here as needs-answer and goes
 *    to `onNeedsAnswer`; `answer` sends it again, `dismiss` drops it. A
 *    product that changed goes to `onProductChanged`. Any other refusal is
 *    `onFailed`: the save leaves the queue (the page keeps the placements).
 *  - Offline: nothing sends; on reconnect the queue drains in order.
 *  - The queue is mirrored to sessionStorage (try/catch, no tokens, no
 *    secrets: product ids, slot keys, placements, content hashes, file
 *    names). A reload restores the saves whose files were stored and drops
 *    the ones whose file never finished, naming them (`onLost`).
 *  - Publish state: getSaveStatus for at most the 20 most recently saved
 *    products, every 2 s, only while one of their links is pending or not
 *    yet read; a failed or stalled link is reported once (`onPublishFailed`)
 *    and Retry (`retryPublish`) calls publishUse once per press (the server
 *    dedupes a repeat). A read that finds a link newly published says so
 *    once (`onPublished`, every such product of that read together), so
 *    Product sync's cached artwork reads are refreshed.
 *
 * The core (`createStudioSaveQueue`) is plain TypeScript; the hook wires it
 * to React, the window's online events, the toasts and the page.
 */
import { SKIP_GLOBAL_ERROR_REPORT_META } from '@/lib/query-error-meta';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ManualPrintPlacement } from '@threadheads/shared';
import type { RouterInputs, RouterOutputs } from '@/lib/trpc';
import { studioErrorLine, type StudioErrorLine } from '../lib/studio-errors';
import type { StudioUploadState } from '../lib/studio-upload-store';
import type { CellKey } from '../lib/studio-cells';

type SavePlacementsInput = RouterInputs['uploadStudio']['savePlacements'];
type ServerSave = SavePlacementsInput['saves'][number];
type PlaceSaveInput = Extract<ServerSave, { action: 'place' }>;
export type StudioStatusLink = RouterOutputs['uploadStudio']['getSaveStatus']['links'][number];
export type StudioLinkState = StudioStatusLink['state'];

/** Which slot x platen a save is for. */
export interface StudioSaveSlot {
  platen: PlaceSaveInput['platen'];
  side: PlaceSaveInput['side'];
  tone: PlaceSaveInput['tone'];
}

/**
 * One slot x platen to save from a file: 'place' (this slot's position, or
 * the shared one it prints from with `confirmOtherUses`), or 'own' (91-10,
 * "Make a new one": this product gets its own position). It carries its
 * file's sha256 or the upload it waits for.
 */
export interface StudioFileSave extends StudioSaveSlot {
  action: 'place' | 'own';
  placement: ManualPrintPlacement;
  /** Null while the file is still uploading; `uploadKey` names that upload. */
  sourceSha256: string | null;
  uploadKey: string | null;
  /**
   * Wave 5 review fix 1: the file on `uploadKey` this save was placed for
   * (its `fileId`). A newer file on the key never takes its place.
   */
  uploadFileId?: number;
  sourceFileName: string;
  placementId?: string;
  expectedVersion?: number;
  /** 'place' only: a confirmed change to a position other products print from (the exact count). */
  confirmOtherUses?: number;
}

/**
 * 91-10 'use' ("Use this file"): link the slot to a design ID's saved
 * position. `seen` is that position as the operator saw it, so "Keep the one
 * I saw, for this product only" can save it as the product's own.
 */
export interface StudioUseSave extends StudioSaveSlot {
  action: 'use';
  placementId: string;
  expectedVersion: number;
  seen: { placement: ManualPrintPlacement; sourceSha256: string; sourceFileName: string };
}

export type StudioQueuedSave = StudioFileSave | StudioUseSave;

/** A save's slot key (`side|tone|platen`, the screen's cell key). */
export const saveSlotKey = (s: StudioSaveSlot): CellKey => `${s.side}|${s.tone}|${s.platen}`;

/** A file save as the server takes it (its sha256 known). */
export function toServerSave(s: StudioQueuedSave): ServerSave {
  const slot = { platen: s.platen, side: s.side, tone: s.tone };
  if (s.action === 'use') return { ...slot, action: 'use', placementId: s.placementId, expectedVersion: s.expectedVersion };
  const base = {
    ...slot,
    placement: s.placement,
    sourceSha256: s.sourceSha256!,
    sourceFileName: s.sourceFileName,
    ...(s.placementId !== undefined && s.expectedVersion !== undefined
      ? { placementId: s.placementId, expectedVersion: s.expectedVersion }
      : {}),
  };
  if (s.action === 'own') return { ...base, action: 'own' };
  return { ...base, action: 'place', ...(s.confirmOtherUses !== undefined ? { confirmOtherUses: s.confirmOtherUses } : {}) };
}

/** A shared 'place' turned into this product's own position (the safe answer): no position id, no count. */
export function asOwnSave(s: StudioFileSave): StudioFileSave {
  const { placementId: _id, expectedVersion: _v, confirmOtherUses: _c, ...rest } = s;
  return { ...rest, action: 'own' };
}

export interface StudioSaveRequest {
  /** 'putBack': Put back last position (one slot, stays on the product). */
  kind: 'save' | 'putBack';
  productId: string;
  productTitle: string;
  locationId: string;
  slotsKey: string;
  saves: StudioQueuedSave[];
}

export type StudioQueueState = 'waiting' | 'ready' | 'sending' | 'needsAnswer';

export interface StudioRefusalCause {
  code: string;
  reason?: string;
  count?: number;
  /** The slot x platen the refusal is about (wave 5 review fix 3), when the server named one. */
  slot?: CellKey;
}

export interface StudioQueueEntry extends StudioSaveRequest {
  id: string;
  state: StudioQueueState;
  /** Put back: a second press while unsent replaces this entry. */
  replaceKey: string | null;
  /** Set while needs-answer. */
  cause: StudioRefusalCause | null;
  /**
   * Final fix A5: the signed-in user + org it was queued in (`storageScope`).
   * Only entries of the current scope are sent; another org's wait until the
   * operator is back in it. Null when the queue has no scope (tests).
   */
  scope?: string | null;
  /** Final fix A2: it was sent at least once (a later save never merges into it). */
  tried?: boolean;
}

/** The refusals the reuse dialogs (91-10) answer; the save waits for the answer. */
export const NEEDS_ANSWER_CODES: ReadonlySet<string> = new Set([
  'UPLOAD_STUDIO_SHARED_CHANGE_UNCONFIRMED',
  'UPLOAD_STUDIO_PLACEMENT_CONFLICT',
  'UPLOAD_STUDIO_TOO_MANY_LINKED',
]);

/** A link at the version it published (a newer publish of the same link is a new key). */
const publishedKey = (l: StudioStatusLink): string =>
  `${l.productId}|${l.side}|${l.tone}|${l.platen}|${l.publishedVersion ?? l.targetVersion}`;

/** The most products whose publish state is read (getSaveStatus's bound). */
export const STATUS_PRODUCTS_MAX = 20;
export const STATUS_POLL_MS = 2000;
/**
 * The 2 s status read (wave 5 review fix 7): never retried by the query
 * layer (the next tick is the retry, and the queue never overlaps two
 * reads) and never reported to Sentry on a flaky link (a breadcrumb only).
 */
export const STATUS_READ_OPTIONS = { staleTime: 0, retry: false, meta: SKIP_GLOBAL_ERROR_REPORT_META } as const;
export const SAVE_QUEUE_STORAGE_KEY = 'uploadStudio.saveQueue.v1';

export interface StudioSaveQueueEvents {
  onSaving?: (entry: StudioQueueEntry) => void;
  onSent?: (entry: StudioQueueEntry) => void;
  onFailed?: (entry: StudioQueueEntry, line: StudioErrorLine) => void;
  onNeedsAnswer?: (entry: StudioQueueEntry, cause: StudioRefusalCause) => void;
  onProductChanged?: (entry: StudioQueueEntry, line: StudioErrorLine) => void;
  /** A link of a recently saved product finished failed, or stalled. Once per link + version. */
  onPublishFailed?: (productId: string, productTitle: string) => void;
  /**
   * One status read found links of these products newly published (a
   * version the last read had not seen published). Once per read.
   */
  onPublished?: (productIds: string[]) => void;
  /** Wave 5 review fix 1: some of a waiting save's platens were taken out, a newer file replaced theirs. */
  onFileReplaced?: (entry: StudioQueueEntry) => void;
  /** A save left the queue with nothing to send (every platen's file was replaced). */
  onGone?: (entry: StudioQueueEntry) => void;
  /** A restored save whose file never finished uploading was dropped. */
  onLost?: (productId: string, productTitle: string) => void;
  /** Back online with this many products still to save. */
  onBackOnline?: (products: number) => void;
}

export interface StudioSaveQueueDeps {
  uploads: { get: (key: string) => StudioUploadState; subscribe: (listener: () => void) => () => void };
  send: (input: SavePlacementsInput) => Promise<unknown>;
  fetchStatus: (productIds: string[]) => Promise<{ links: StudioStatusLink[] }>;
  publishUse: (input: { productId: string; platen: StudioQueuedSave['platen']; side: StudioQueuedSave['side']; tone: StudioQueuedSave['tone'] }) => Promise<unknown>;
  events?: StudioSaveQueueEvents;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  /**
   * Whose mirror (wave 5 review nit): the signed-in user + org, so a reload
   * only restores the saves that user queued in that org. Null: no mirror
   * yet (not signed in). Absent: one shared key (tests).
   */
  storageScope?: () => string | null;
  online?: boolean;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface StudioSaveQueueSnapshot {
  entries: readonly StudioQueueEntry[];
  online: boolean;
  /** The scope now (final fix A5): the page asks and waits only for this scope's entries. */
  scope: string | null;
  /** Link states of the recently saved products, by product id. */
  links: ReadonlyMap<string, readonly StudioStatusLink[]>;
}

export interface StudioSaveQueue {
  getSnapshot: () => StudioSaveQueueSnapshot;
  subscribe: (listener: () => void) => () => void;
  enqueue: (request: StudioSaveRequest, options?: { replaceKey?: string }) => string;
  /**
   * The operator's answer to a needs-answer save: the exact count confirmed on
   * every 'place', or the saves replaced (91-10: 'use' at the new version,
   * 'own' for this product only). It is sent again in its place in the queue.
   */
  answer: (id: string, patch: { confirmOtherUses?: number; slot?: CellKey; saves?: StudioQueuedSave[] }) => void;
  dismiss: (id: string) => void;
  setOnline: (online: boolean) => void;
  retryPublish: (productId: string, productTitle: string, slot: Pick<StudioQueuedSave, 'platen' | 'side' | 'tone'>) => Promise<void>;
  /** Restore the sessionStorage mirror (at page start; an entry already here is skipped). */
  restore: () => void;
  /** Start (again) listening to uploads and sending. */
  start: () => void;
  /** Stop listening and sending (the page left). A send in flight still settles. */
  stop: () => void;
  /**
   * Final fix A5: the user or org changed. Entries of the new scope go on,
   * every other one is held (sent once the operator is back in its org).
   */
  rescope: () => void;
  /**
   * Final fix A4: the studio page closed. Every save still waiting for an
   * answer leaves the queue as failed (`onFailed`, its toast has Open it).
   */
  failAwaiting: () => void;
}

function causeOf(err: unknown): StudioRefusalCause | null {
  const cause = (err as { data?: { cause?: unknown } } | null | undefined)?.data?.cause as
    | { code?: unknown; reason?: unknown; count?: unknown; platen?: unknown; side?: unknown; tone?: unknown }
    | undefined;
  if (!cause || typeof cause.code !== 'string') return null;
  const named = typeof cause.platen === 'string' && typeof cause.side === 'string' && typeof cause.tone === 'string';
  return {
    code: cause.code,
    ...(typeof cause.reason === 'string' ? { reason: cause.reason } : {}),
    ...(typeof cause.count === 'number' ? { count: cause.count } : {}),
    ...(named ? { slot: `${cause.side as string}|${cause.tone as string}|${cause.platen as string}` as CellKey } : {}),
  };
}

/** No answer at all (the request never reached the server). */
function isNoAnswer(err: unknown): boolean {
  const e = err as { data?: unknown; meta?: { response?: { status?: unknown } } } | null;
  return !e?.data && typeof e?.meta?.response?.status !== 'number';
}

/** A gateway that did not answer for the server (the proxy, a deploy, a 25 s timeout). */
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/**
 * Wave 5 review fix 8: worth sending again as it is: no answer at all, or a
 * gateway 502 / 503 / 504 with no refusal from the server. A save is safe to
 * send twice (a retry of a save that did land is answered as done: the
 * version moved by this user to these values).
 */
export function isTransientSendError(err: unknown): boolean {
  if (causeOf(err)) return false;
  if (isNoAnswer(err)) return true;
  const e = err as { data?: { httpStatus?: unknown } | null; meta?: { response?: { status?: unknown } } } | null;
  const status = e?.meta?.response?.status ?? e?.data?.httpStatus;
  return typeof status === 'number' && GATEWAY_STATUSES.has(status);
}

/** The wait before sending again after the n-th transient failure in a row: 1 s, 2 s, 4 s ... at most 30 s. */
export const sendBackoffMs = (failures: number): number => Math.min(30_000, 1000 * 2 ** Math.max(0, failures - 1));

const SHA_RE = /^[0-9a-f]{64}$/;
const GID_RE = /^gid:\/\/shopify\/Product\/\d{1,20}$/;

/** A mirrored entry read back; null for anything not in the shape this file writes. */
function readMirrored(raw: unknown): StudioQueueEntry | null {
  const e = raw as Partial<StudioQueueEntry> | null;
  if (!e || typeof e !== 'object') return null;
  if (typeof e.id !== 'string' || typeof e.productId !== 'string' || !GID_RE.test(e.productId)) return null;
  if (typeof e.locationId !== 'string' || typeof e.slotsKey !== 'string' || typeof e.productTitle !== 'string') return null;
  if (e.kind !== 'save' && e.kind !== 'putBack') return null;
  if (!Array.isArray(e.saves) || e.saves.length === 0 || e.saves.length > 12) return null;
  const isPlacement = (p: ManualPrintPlacement | undefined) =>
    !!p && typeof p.x_in === 'number' && typeof p.y_in === 'number' && typeof p.width_in === 'number';
  for (const s of e.saves) {
    if (!s || typeof s.platen !== 'string' || typeof s.side !== 'string' || typeof s.tone !== 'string') return null;
    if (s.action === 'use') {
      if (typeof s.placementId !== 'string' || typeof s.expectedVersion !== 'number') return null;
      if (!s.seen || !isPlacement(s.seen.placement) || typeof s.seen.sourceSha256 !== 'string' || !SHA_RE.test(s.seen.sourceSha256)) return null;
      if (typeof s.seen.sourceFileName !== 'string') return null;
      continue;
    }
    if (s.action !== 'place' && s.action !== 'own') return null;
    if (!isPlacement(s.placement)) return null;
    if (s.sourceSha256 !== null && (typeof s.sourceSha256 !== 'string' || !SHA_RE.test(s.sourceSha256))) return null;
    if (typeof s.sourceFileName !== 'string') return null;
  }
  return {
    id: e.id,
    kind: e.kind,
    productId: e.productId,
    productTitle: e.productTitle,
    locationId: e.locationId,
    slotsKey: e.slotsKey,
    saves: e.saves,
    state: 'ready',
    replaceKey: typeof e.replaceKey === 'string' ? e.replaceKey : null,
    cause: null,
    // A restored save may have been sent before the reload: never merged into.
    tried: true,
  };
}

let idSeq = 0;
const newId = () => `save-${Date.now().toString(36)}-${(idSeq += 1)}`;

export function createStudioSaveQueue(deps: StudioSaveQueueDeps): StudioSaveQueue {
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const events = () => deps.events ?? {};
  const listeners = new Set<() => void>();
  let entries: StudioQueueEntry[] = [];
  let online = deps.online ?? true;
  let sending = false;
  /** Off while the page is gone: nothing new is sent or read. */
  let active = false;
  let unsubscribeUploads: (() => void) | null = null;
  /** Recently saved products, newest first, with their titles and scope. */
  let recent: Array<{ productId: string; title: string; scope: string | null }> = [];
  /**
   * Products saved (or retried) since their last status read, with the mark's
   * number (final fix A6): a read only clears the mark it was asked under, so
   * a read already on its way never clears the mark of a newer save.
   */
  const unread = new Map<string, number>();
  let markSeq = 0;
  let links = new Map<string, readonly StudioStatusLink[]>();
  /** Failed / stalled links already reported (product, slot, version). */
  const reported = new Set<string>();
  let pollTimer: unknown = null;
  /** Transient failures in a row, and the wait before the next send. */
  let failuresInRow = 0;
  let backoffTimer: unknown = null;
  const clearBackoff = () => {
    if (backoffTimer !== null) clearTimer(backoffTimer);
    backoffTimer = null;
  };
  /** The user + org now (final fix A5); null with no scope (tests) or not signed in. */
  const currentScope = (): string | null => (deps.storageScope ? deps.storageScope() : null);
  /** An entry (or recent product) of the scope now. */
  const inScope = (scope: string | null | undefined): boolean => (scope ?? null) === currentScope();
  /** Signed out, or the org not known yet: nothing is sent. */
  const noScope = (): boolean => !!deps.storageScope && currentScope() === null;

  let snapshot: StudioSaveQueueSnapshot = { entries, online, links, scope: currentScope() };

  /** The mirror's key for a scope, or null (nothing mirrored). */
  const keyOfScope = (scope: string | null): string | null => {
    if (!deps.storageScope) return SAVE_QUEUE_STORAGE_KEY;
    return scope ? `${SAVE_QUEUE_STORAGE_KEY}:${scope}` : null;
  };
  /** Keys this queue wrote, so a scope whose saves all went is cleared. */
  const mirroredKeys = new Set<string>();

  const mirror = () => {
    const storage = deps.storage;
    if (!storage) return;
    try {
      // Each entry is mirrored under its OWN scope's key (final fix A5).
      const byKey = new Map<string, StudioQueueEntry[]>();
      for (const e of entries) {
        const key = keyOfScope(e.scope ?? null);
        if (!key) continue;
        byKey.set(key, [...(byKey.get(key) ?? []), e]);
      }
      const nowKey = keyOfScope(currentScope());
      for (const key of [...mirroredKeys, ...(nowKey ? [nowKey] : [])]) {
        if (byKey.has(key)) continue;
        storage.removeItem(key);
        mirroredKeys.delete(key);
      }
      for (const [key, rows] of byKey) {
        // Each save with the sha256 known now: a reload can only send those.
        const out = rows.map((e) => ({
          id: e.id,
          kind: e.kind,
          productId: e.productId,
          productTitle: e.productTitle,
          locationId: e.locationId,
          slotsKey: e.slotsKey,
          replaceKey: e.replaceKey,
          saves: e.saves.map((s) => (s.action === 'use' ? s : { ...s, sourceSha256: s.sourceSha256 ?? shaOf(s) })),
        }));
        storage.setItem(key, JSON.stringify(out));
        mirroredKeys.add(key);
      }
    } catch {
      // Storage full or blocked: the queue in memory is the record.
    }
  };

  const changed = () => {
    snapshot = { entries: [...entries], online, links, scope: currentScope() };
    mirror();
    for (const listener of [...listeners]) listener();
  };

  /** The file on the save's key is no longer the one it was placed for (a newer drop replaced it). */
  const replaced = (save: StudioFileSave): boolean => {
    if (save.sourceSha256 || !save.uploadKey || save.uploadFileId === undefined) return false;
    const file = deps.uploads.get(save.uploadKey).file;
    return !!file && (file.fileId ?? 0) !== save.uploadFileId;
  };

  const shaOf = (save: StudioFileSave): string | null => {
    if (save.sourceSha256) return save.sourceSha256;
    if (!save.uploadKey || replaced(save)) return null;
    const up = deps.uploads.get(save.uploadKey);
    return up.phase === 'stored' && up.file?.sha256 ? up.file.sha256 : null;
  };

  /**
   * The saves with every sha256 known, 'wait' while a file moves, or 'lost'
   * when its upload is gone. A save whose file was replaced by a newer one on
   * its key is taken out (`stale`): its position was placed for the old file.
   */
  const resolve = (entry: StudioQueueEntry): { saves: StudioQueuedSave[] | 'wait' | 'lost'; stale: StudioFileSave[] } => {
    const stale = entry.saves.filter((s): s is StudioFileSave => s.action !== 'use' && replaced(s));
    const live = stale.length > 0 ? entry.saves.filter((s) => !stale.includes(s as StudioFileSave)) : entry.saves;
    if (live.length === 0) return { saves: 'lost', stale };
    const out: StudioQueuedSave[] = [];
    for (const save of live) {
      if (save.action === 'use') {
        out.push(save);
        continue;
      }
      const sha = shaOf(save);
      if (sha) {
        out.push({ ...save, sourceSha256: sha, uploadKey: null, uploadFileId: undefined });
        continue;
      }
      const up = save.uploadKey ? deps.uploads.get(save.uploadKey) : null;
      if (!up || !up.file) return { saves: 'lost', stale };
      if (stale.length > 0) entry.saves = live;
      return { saves: 'wait', stale };
    }
    return { saves: out, stale };
  };

  const remove = (id: string) => {
    entries = entries.filter((e) => e.id !== id);
  };

  const track = (productId: string, title: string, scope: string | null) => {
    recent = [{ productId, title, scope }, ...recent.filter((r) => r.productId !== productId)].slice(0, STATUS_PRODUCTS_MAX);
    unread.set(productId, (markSeq += 1));
    schedulePoll();
  };

  /** The recent products of the scope now (another org's are never read under this one). */
  const recentInScope = () => recent.filter((r) => inScope(r.scope));

  const anyPending = () =>
    recentInScope().some((r) => unread.has(r.productId) || (links.get(r.productId) ?? []).some((l) => l.state === 'pending'));

  /** A read is on its way: the next is scheduled when it answers, never beside it. */
  let polling = false;
  function schedulePoll() {
    if (!active || polling || pollTimer !== null || !anyPending()) return;
    pollTimer = setTimer(() => void poll(), STATUS_POLL_MS);
  }

  async function poll() {
    pollTimer = null;
    const scoped = recentInScope();
    if (!active || scoped.length === 0) return;
    if (!online) {
      schedulePoll();
      return;
    }
    const asked = scoped.map((r) => r.productId);
    // The marks as they were when asked: a save after this point keeps its own.
    const marks = new Map(asked.map((id) => [id, unread.get(id)] as const));
    polling = true;
    try {
      const answer = await deps.fetchStatus(asked);
      if (!active) return;
      // A product saved again while the read was on its way: this answer is
      // older than that save, so it is dropped for that product (final fix A6).
      const stale = new Set(asked.filter((id) => unread.get(id) !== marks.get(id)));
      // What the last read had already seen published (by link and version).
      const seenPublished = new Set<string>();
      for (const id of asked) for (const l of links.get(id) ?? []) if (l.state === 'published') seenPublished.add(publishedKey(l));
      const published = new Set<string>();
      for (const link of answer.links) {
        if (!stale.has(link.productId) && link.state === 'published' && !seenPublished.has(publishedKey(link))) {
          published.add(link.productId);
        }
      }
      const next = new Map(links);
      for (const id of asked) if (!stale.has(id)) next.set(id, []);
      for (const link of answer.links) {
        if (stale.has(link.productId)) continue;
        next.set(link.productId, [...(next.get(link.productId) ?? []), link]);
      }
      links = next;
      for (const id of asked) if (!stale.has(id)) unread.delete(id);
      for (const link of answer.links) {
        if (stale.has(link.productId)) continue;
        if (link.state !== 'failed' && link.state !== 'stalled') continue;
        const key = `${link.productId}|${link.side}|${link.tone}|${link.platen}|${link.targetVersion}`;
        if (reported.has(key)) continue;
        reported.add(key);
        const title = recent.find((r) => r.productId === link.productId)?.title ?? '';
        events().onPublishFailed?.(link.productId, title);
      }
      changed();
      if (published.size > 0) events().onPublished?.([...published]);
    } catch {
      // A failed read tries again on the next tick.
    } finally {
      polling = false;
    }
    schedulePoll();
  }

  /** A save emptied by newer files: its Saving toast goes (onSent never comes). */
  const toastGone = (entry: StudioQueueEntry) => events().onGone?.(entry);

  function pump() {
    if (!active || sending || !online || backoffTimer !== null || noScope()) return;
    const blocked = new Set<string>();
    let moved = false;
    for (const entry of [...entries]) {
      // Another org's (or user's) save waits until the operator is back in it (A5).
      if (!inScope(entry.scope)) continue;
      if (blocked.has(entry.productId)) continue;
      if (entry.state === 'waiting') {
        const { saves, stale } = resolve(entry);
        // A newer file on the key replaces what was queued for the old one: the
        // operator saves the new one (it merges into this save while unsent).
        if (stale.length > 0) {
          moved = true;
          events().onFileReplaced?.(entry);
        }
        if (saves === 'lost') {
          remove(entry.id);
          moved = true;
          if (stale.length === 0) events().onFailed?.(entry, { key: 'uploadStudio:save.fileLost' });
          else toastGone(entry);
          continue;
        }
        if (saves !== 'wait') {
          entry.saves = saves;
          entry.state = 'ready';
          moved = true;
        }
      }
      if (entry.state === 'ready') {
        void send(entry);
        return;
      }
      // Waiting for its file, or for an answer: never overtaken by its own product's later saves.
      blocked.add(entry.productId);
    }
    if (moved) changed();
  }

  /**
   * A save just went through: a later unsent save of the same product for the
   * same slot was made from the same saved position (the operator went back
   * with P while the first one was on its way). It now names the position
   * the first one wrote, so it is not refused as a change made meanwhile.
   */
  function rebase(sent: StudioQueueEntry, answer: unknown) {
    const results = (answer as { results?: unknown } | null | undefined)?.results;
    if (!Array.isArray(results)) return;
    const written = new Map<CellKey, { placementId: string; placementVersion: number }>();
    for (const r of results as Array<Partial<StudioSaveSlot & { placementId: unknown; placementVersion: unknown }>>) {
      if (!r || typeof r.placementId !== 'string' || typeof r.placementVersion !== 'number') continue;
      written.set(saveSlotKey(r as StudioSaveSlot), { placementId: r.placementId, placementVersion: r.placementVersion });
    }
    const sentBy = new Map(sent.saves.map((s) => [saveSlotKey(s), s] as const));
    for (const later of entries) {
      if (later.productId !== sent.productId || later.state === 'sending' || (later.scope ?? null) !== (sent.scope ?? null)) continue;
      later.saves = later.saves.map((s) => {
        const first = sentBy.get(saveSlotKey(s));
        const now = written.get(saveSlotKey(s));
        if (!first || !now || s.action === 'use' || first.action === 'use') return s;
        const sameBase = s.placementId === first.placementId && s.expectedVersion === first.expectedVersion;
        if (!sameBase) return s;
        // An 'own' that went through made the product's own position: a later save of the slot bumps it.
        return { ...s, placementId: now.placementId, expectedVersion: now.placementVersion };
      });
    }
  }

  async function send(entry: StudioQueueEntry) {
    sending = true;
    entry.state = 'sending';
    entry.tried = true;
    changed();
    try {
      const answer = await deps.send({
        productId: entry.productId,
        locationId: entry.locationId,
        expectedSlotsKey: entry.slotsKey,
        saves: entry.saves.map(toServerSave),
      });
      failuresInRow = 0;
      remove(entry.id);
      rebase(entry, answer);
      track(entry.productId, entry.productTitle, entry.scope ?? null);
      events().onSent?.(entry);
    } catch (err) {
      const cause = causeOf(err);
      const offlineNow = !online || (typeof navigator !== 'undefined' && navigator.onLine === false);
      if (cause && NEEDS_ANSWER_CODES.has(cause.code)) {
        entry.state = 'needsAnswer';
        entry.cause = cause;
        events().onNeedsAnswer?.(entry, cause);
      } else if (!cause && isNoAnswer(err) && offlineNow) {
        // Offline: it waits and goes again in order on reconnect.
        entry.state = 'ready';
      } else if (isTransientSendError(err)) {
        // Online but no answer, or a gateway error: it stays first in line and
        // goes again after a wait that grows (the whole queue keeps its order).
        entry.state = 'ready';
        failuresInRow += 1;
        clearBackoff();
        backoffTimer = setTimer(() => {
          backoffTimer = null;
          pump();
        }, sendBackoffMs(failuresInRow));
      } else if (cause?.code === 'UPLOAD_STUDIO_PRODUCT_CHANGED') {
        remove(entry.id);
        events().onProductChanged?.(entry, studioErrorLine(err));
      } else {
        remove(entry.id);
        events().onFailed?.(entry, studioErrorLine(err));
      }
    } finally {
      sending = false;
      changed();
      pump();
    }
  }

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    enqueue: (request, options = {}) => {
      const replaceKey = options.replaceKey ?? null;
      const scope = currentScope();
      const sameProduct = (e: StudioQueueEntry) => e.productId === request.productId && (e.scope ?? null) === scope;
      if (request.kind === 'save' && !replaceKey) {
        // The same product's save not sent yet (P back to it, then Save and
        // next again): one save, the latest values win slot by slot. Never a
        // second save at the same version. Final fix A2: only into a save
        // never tried, and only while it is that product's LAST entry, so a
        // merged save never jumps ahead of a later Put back.
        const last = [...entries].reverse().find(sameProduct);
        const unsent =
          last && last.kind === 'save' && last.replaceKey === null && !last.tried && (last.state === 'waiting' || last.state === 'ready')
            ? last
            : undefined;
        if (unsent) {
          const latest = new Map(request.saves.map((s) => [saveSlotKey(s), s] as const));
          unsent.saves = [...unsent.saves.filter((s) => !latest.has(saveSlotKey(s))), ...request.saves];
          unsent.slotsKey = request.slotsKey;
          unsent.productTitle = request.productTitle;
          unsent.locationId = request.locationId;
          unsent.state = 'waiting';
          changed();
          pump();
          return unsent.id;
        }
      }
      if (replaceKey) {
        const unsent = entries.find(
          (e) => e.replaceKey === replaceKey && (e.scope ?? null) === scope && (e.state === 'waiting' || e.state === 'ready'),
        );
        if (unsent) {
          unsent.saves = request.saves;
          unsent.state = 'waiting';
          changed();
          pump();
          return unsent.id;
        }
      }
      const entry: StudioQueueEntry = { ...request, id: newId(), state: 'waiting', replaceKey, cause: null, scope, tried: false };
      entries = [...entries, entry];
      events().onSaving?.(entry);
      changed();
      pump();
      return entry.id;
    },

    answer: (id, patch) => {
      const entry = entries.find((e) => e.id === id && e.state === 'needsAnswer');
      if (!entry) return;
      if (patch.saves) entry.saves = patch.saves;
      else if (patch.confirmOtherUses !== undefined) {
        // The count is one slot's: it confirms that save only (a second
        // shared platen with another count is asked for on its own).
        const count = patch.confirmOtherUses;
        const only = patch.slot ?? entry.cause?.slot;
        entry.saves = entry.saves.map((s) =>
          s.action === 'place' && (!only || saveSlotKey(s) === only) ? { ...s, confirmOtherUses: count } : s,
        );
      }
      // A file save may name an upload still on its way: it waits for it again.
      entry.state = 'waiting';
      entry.cause = null;
      events().onSaving?.(entry);
      changed();
      pump();
    },

    dismiss: (id) => {
      if (!entries.some((e) => e.id === id && e.state !== 'sending')) return;
      remove(id);
      changed();
      pump();
    },

    setOnline: (next) => {
      if (next === online) return;
      online = next;
      changed();
      if (next) {
        // Back online: no wait left over from the flaky link.
        clearBackoff();
        const products = new Set(entries.map((e) => e.productId)).size;
        if (products > 0) events().onBackOnline?.(products);
        pump();
        schedulePoll();
      }
    },

    retryPublish: async (productId, productTitle, slot) => {
      try {
        await deps.publishUse({ productId, platen: slot.platen, side: slot.side, tone: slot.tone });
      } catch (err) {
        events().onFailed?.(
          { id: `retry:${productId}`, kind: 'save', productId, productTitle, locationId: '', slotsKey: '', saves: [], state: 'ready', replaceKey: null, cause: null },
          studioErrorLine(err),
        );
      }
      // Read its state again: a link reported failed may now be pending.
      for (const key of [...reported]) if (key.startsWith(`${productId}|${slot.side}|${slot.tone}|${slot.platen}|`)) reported.delete(key);
      track(productId, productTitle, currentScope());
    },

    restore: () => {
      const storage = deps.storage;
      const scope = currentScope();
      const key = keyOfScope(scope);
      if (!storage || !key) return;
      let rows: unknown;
      try {
        const raw = storage.getItem(key);
        rows = raw ? JSON.parse(raw) : null;
      } catch {
        rows = null;
      }
      if (!Array.isArray(rows)) return;
      const restored: StudioQueueEntry[] = [];
      for (const row of rows) {
        const entry = readMirrored(row);
        if (!entry) continue;
        if (entries.some((e) => e.id === entry.id)) continue;
        if (entry.saves.some((s) => s.action !== 'use' && !s.sourceSha256)) {
          events().onLost?.(entry.productId, entry.productTitle);
          continue;
        }
        restored.push({ ...entry, scope, saves: entry.saves.map((s) => (s.action === 'use' ? s : { ...s, uploadKey: null })) });
      }
      entries = [...restored, ...entries];
      for (const entry of restored) events().onSaving?.(entry);
      changed();
      pump();
    },

    start: () => {
      if (active) return;
      active = true;
      unsubscribeUploads = deps.uploads.subscribe(() => {
        if (entries.some((e) => e.state === 'waiting')) pump();
      });
      pump();
      schedulePoll();
    },

    stop: () => {
      active = false;
      unsubscribeUploads?.();
      unsubscribeUploads = null;
      if (pollTimer !== null) clearTimer(pollTimer);
      pollTimer = null;
      clearBackoff();
    },

    rescope: () => {
      // The flaky-link wait belonged to the other scope's save.
      clearBackoff();
      failuresInRow = 0;
      changed();
      pump();
      schedulePoll();
    },

    failAwaiting: () => {
      const waiting = entries.filter((e) => e.state === 'needsAnswer');
      if (waiting.length === 0) return;
      entries = entries.filter((e) => e.state !== 'needsAnswer');
      for (const entry of waiting) {
        const cause = entry.cause;
        entry.cause = null;
        events().onFailed?.(entry, studioErrorLine(cause ? { data: { cause } } : null));
      }
      changed();
      pump();
    },
  };
}

// ---------------------------------------------------------------------------
// The hook: the page's one queue, its toasts and the window's connection.
// ---------------------------------------------------------------------------

export interface UseStudioSaveQueueOptions {
  uploads: StudioSaveQueueDeps['uploads'];
  send: StudioSaveQueueDeps['send'];
  fetchStatus: StudioSaveQueueDeps['fetchStatus'];
  publishUse: StudioSaveQueueDeps['publishUse'];
  /** "Open it" on a failed save's toast. */
  openProduct: (productId: string) => void;
  /** The signed-in user + org the mirror belongs to (null until known). */
  storageScope?: string | null;
  /**
   * Final fix A5: the user + org NOW, read at the moment a save is sent (the
   * org switcher changes the request header before React re-renders).
   * Preferred over `storageScope` when given.
   */
  scopeNow?: () => string | null;
  onSent?: (entry: StudioQueueEntry) => void;
  /** A status read found links of these products newly published. */
  onPublished?: (productIds: string[]) => void;
  /**
   * True when the page asks the operator (the save stays needs-answer until
   * `answer` or `dismiss`). False: the save is dropped as failed (its toast).
   */
  onNeedsAnswer?: (entry: StudioQueueEntry, cause: StudioRefusalCause) => boolean;
  /** True when the page showed the changed note itself (the product is open). */
  onProductChanged?: (entry: StudioQueueEntry) => boolean;
  onFailed?: (entry: StudioQueueEntry) => void;
}

export interface StudioSaveQueueApi extends StudioSaveQueueSnapshot {
  /** The queue as it is now (an event handler reads it after its own enqueue). */
  getSnapshot: StudioSaveQueue['getSnapshot'];
  enqueue: StudioSaveQueue['enqueue'];
  answer: StudioSaveQueue['answer'];
  dismiss: StudioSaveQueue['dismiss'];
  retryPublish: StudioSaveQueue['retryPublish'];
  /** The publish state of a product's slot x platen, when a recent read has it. */
  linkStates: (productId: string) => ReadonlyMap<CellKey, StudioLinkState>;
}

function sessionStore(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * The one queue, its toasts and the window's connection. Wave 5 review fix
 * 2: owned by `StudioSessionProvider` above the routes, so it keeps sending
 * (and its toasts resolve) after the operator leaves the studio by any link.
 */
export function useStudioSaveQueueOwner(options: UseStudioSaveQueueOptions): StudioSaveQueue {
  const { t } = useTranslation('uploadStudio');
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const tRef = useRef(t);
  tRef.current = t;

  const [queue] = useState(() => {
    // Assigned below; the events only run after it exists.
    let self: StudioSaveQueue | null = null;
    const o = () => optionsRef.current;
    const tt = (key: string, values?: Record<string, unknown>) => tRef.current(key, values);
    const failedToast = (entry: Pick<StudioQueueEntry, 'id' | 'productId' | 'productTitle'>, line: StudioErrorLine | null) =>
      toast.error(tt('save.failed', { product: entry.productTitle }), {
        id: entry.id,
        description: line ? tt(line.key, line.values) : undefined,
        duration: Infinity,
        action: { label: tt('save.open'), onClick: () => o().openProduct(entry.productId) },
      });
    const created = createStudioSaveQueue({
      uploads: options.uploads,
      send: (input) => o().send(input),
      fetchStatus: (ids) => o().fetchStatus(ids),
      publishUse: (input) => o().publishUse(input),
      storage: sessionStore(),
      storageScope: () => {
        const now = o().scopeNow;
        return now ? now() : (o().storageScope ?? null);
      },
      online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
      events: {
        onSaving: (entry) => toast.loading(tt('save.saving', { product: entry.productTitle }), { id: entry.id }),
        onSent: (entry) => {
          toast.success(tt('save.saved', { product: entry.productTitle, count: entry.saves.length }), { id: entry.id, duration: 4000 });
          o().onSent?.(entry);
        },
        onFailed: (entry, line) => {
          failedToast(entry, line);
          o().onFailed?.(entry);
        },
        onNeedsAnswer: (entry, cause) => {
          toast.dismiss(entry.id);
          if (o().onNeedsAnswer?.(entry, cause)) return;
          self?.dismiss(entry.id);
          failedToast(entry, studioErrorLine({ data: { cause } }));
          o().onFailed?.(entry);
        },
        onProductChanged: (entry, line) => {
          if (o().onProductChanged?.(entry)) toast.dismiss(entry.id);
          else failedToast(entry, line);
          o().onFailed?.(entry);
        },
        onPublishFailed: (productId, productTitle) => failedToast({ id: `publish:${productId}`, productId, productTitle }, null),
        onPublished: (productIds) => o().onPublished?.(productIds),
        onFileReplaced: (entry) =>
          toast(tt('save.fileReplaced', { product: entry.productTitle }), { id: `replaced:${entry.productId}` }),
        onGone: (entry) => toast.dismiss(entry.id),
        onLost: (productId, productTitle) =>
          failedToast({ id: `lost:${productId}`, productId, productTitle }, { key: 'uploadStudio:save.fileLost' }),
        onBackOnline: (count) => toast(tt('online', { count })),
      },
    });
    self = created;
    return created;
  });

  useEffect(() => {
    queue.start();
    queue.restore();
    const on = () => queue.setOnline(true);
    const off = () => queue.setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      queue.stop();
    };
  }, [queue]);
  return queue;
}

/** A page's view of the queue: its snapshot, kept current. */
export function useStudioSaveQueueView(queue: StudioSaveQueue): StudioSaveQueueApi {
  const snapshot = useSyncExternalStore(queue.subscribe, queue.getSnapshot, queue.getSnapshot);
  const linkStates = (productId: string): ReadonlyMap<CellKey, StudioLinkState> => {
    const out = new Map<CellKey, StudioLinkState>();
    for (const l of snapshot.links.get(productId) ?? []) out.set(`${l.side}|${l.tone}|${l.platen}` as CellKey, l.state);
    return out;
  };

  return {
    ...snapshot,
    /** The queue as it is now (an event handler reads it after its own enqueue). */
    getSnapshot: queue.getSnapshot,
    enqueue: queue.enqueue,
    answer: queue.answer,
    dismiss: queue.dismiss,
    retryPublish: queue.retryPublish,
    linkStates,
  };
}
